import React, { useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd';

const DD_TOKEN_TYPE = 'DD_TOKEN';

type DropSpacing = {
  ml: boolean;
  mr: boolean;
};

export type DragDropMode = 'code_inline' | 'code_order_line' | 'text_inline' | 'row_match';

export interface DragTokenItem {
  source: 'bank' | 'slot';
  fromSlotIndex?: number | null;
  token: string;
  instanceId?: number;
}

export type DragDropSlotValue = {
  instanceId: number;
  token: string;
};

export type DragDropEngineState = {
  slots: Array<DragDropSlotValue | null>;
  bankTokens: string[];
};

const DraggableToken: React.FC<{
  token: string;
  disabled: boolean;
  onClick: () => void;
  dragItem?: DragTokenItem;
  className?: string;
}> = ({ token, disabled, onClick, dragItem, className }) => {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: DD_TOKEN_TYPE,
      item: dragItem ?? ({ source: 'bank', token, fromSlotIndex: null } as DragTokenItem),
      canDrag: !disabled,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [token, disabled, dragItem],
  );

  return (
    <button
      ref={dragRef}
      type="button"
      className={[
        'inline-flex items-center rounded-full border px-3 py-1 text-sm',
        'bg-white shadow-sm hover:bg-gray-50 active:scale-[0.99] transition',
        disabled ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-blue-200 text-blue-800 cursor-grab',
        isDragging ? 'opacity-50' : '',
        className ?? '',
      ].join(' ')}
      onClick={onClick}
      disabled={disabled}
    >
      {token}
    </button>
  );
};

const DropSlot: React.FC<{
  slotIndex: number;
  tokenItem: DragDropSlotValue | null;
  disabled: boolean;
  onDropItem: (item: DragTokenItem) => void;
  onClear: () => void;
  expectedToken?: string | null;
  revealGate: boolean;
  spacing?: DropSpacing;
  appearance?: 'default' | 'code';
}> = ({ slotIndex, tokenItem, disabled, onDropItem, onClear, expectedToken, revealGate, spacing, appearance = 'default' }) => {
  const [{ isOver, canDrop }, dropRef] = useDrop(
    () => ({
      accept: DD_TOKEN_TYPE,
      canDrop: () => !disabled,
      drop: (item: DragTokenItem) => {
        if (!disabled) onDropItem(item);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [disabled, onDropItem],
  );

  const showActive = isOver && canDrop && !disabled;
  const tokenText = tokenItem?.token ?? '';
  const isCorrect = revealGate && typeof expectedToken === 'string' && expectedToken.length > 0
    ? tokenText === expectedToken
    : null;
  const marginClass = spacing
    ? `${spacing.ml ? 'ml-1' : 'ml-0'} ${spacing.mr ? 'mr-1' : 'mr-0'}`
    : 'mx-1';
  const isCode = appearance === 'code';

  return (
    <span ref={dropRef} className={`${marginClass} inline-flex items-center align-middle`}>
      <div
        className={[
          isCode
            ? 'min-w-[52px] rounded-md border border-dashed px-2 py-1 text-sm min-h-[28px] inline-flex items-center transition'
            : 'min-w-[140px] rounded-lg border px-3 py-2 text-xs min-h-[36px] inline-flex items-center transition',
          showActive ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200',
          !disabled && !showActive ? 'hover:border-gray-300' : '',
          revealGate && isCorrect === true ? 'border-green-400 bg-green-50' : '',
          revealGate && isCorrect === false ? 'border-red-400 bg-red-50' : '',
          'focus-within:ring-2 focus-within:ring-indigo-200 focus-within:ring-offset-0',
          disabled ? 'opacity-75' : '',
        ].join(' ')}
      >
        {tokenText ? (
          <DraggableToken
            token={tokenText}
            disabled={disabled}
            dragItem={{
              source: 'slot',
              token: tokenText,
              instanceId: tokenItem?.instanceId,
              fromSlotIndex: slotIndex,
            }}
            onClick={() => {}}
            className={isCode ? 'shadow-none border-gray-300 px-2 py-0.5 text-sm' : 'shadow-none border-gray-300'}
          />
        ) : (
          <span className={isCode ? 'text-gray-400' : 'text-gray-400'}>{isCode ? '____' : 'Drop here...'}</span>
        )}
      </div>
      {tokenText && (
        <button
          type="button"
          onClick={disabled ? undefined : onClear}
          disabled={Boolean(disabled)}
          className={isCode
            ? 'ml-1 text-xs text-gray-400 hover:text-gray-200 disabled:opacity-50'
            : 'ml-2 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 px-2 py-1 rounded hover:bg-gray-100'}
          aria-label="Clear"
        >
          ✕
        </button>
      )}
      {revealGate && isCorrect !== null && (
        <span className={isCorrect ? 'ml-1 text-green-700 text-xs' : 'ml-1 text-red-700 text-xs'}>
          {isCorrect ? '✅' : '❌'}
        </span>
      )}
    </span>
  );
};

const lastVisibleChar = (value: string) => {
  const match = value.match(/\S(?=\s*$)/);
  return match ? match[0] : '';
};

const firstVisibleChar = (value: string) => {
  const match = value.match(/\S/);
  return match ? match[0] : '';
};

const codeSlotSpacing = (left: string, right: string): DropSpacing => {
  const prev = lastVisibleChar(left);
  const next = firstVisibleChar(right);
  let ml = true;
  let mr = true;
  if (prev === '(') ml = false;
  if (next === ')' || next === ',' || next === ':' || next === ';') mr = false;
  return { ml, mr };
};

const renderCodeStrip = (
  segments: string[],
  blankCount: number,
  slots: Array<DragDropSlotValue | null>,
  disabled: boolean,
  revealGate: boolean,
  expectedTokens: Array<string | null>,
  onDropItem: (slotIndex: number, item: DragTokenItem) => void,
  onClear: (slotIndex: number) => void,
) => {
  const inlineSlotCount = Math.max(Math.min(blankCount, segments.length - 1), 0);
  return (
    <pre className="rounded-lg bg-slate-900 text-slate-100 p-3 font-mono text-sm overflow-x-auto">
      <span className="inline-flex flex-nowrap items-center whitespace-pre">
        {segments.map((segment, idx) => (
          <React.Fragment key={`code-${idx}`}>
            <span className="whitespace-pre">{segment}</span>
            {idx < inlineSlotCount ? (
              <DropSlot
                slotIndex={idx}
                tokenItem={slots[idx] ?? null}
                disabled={disabled}
                onDropItem={(item) => onDropItem(idx, item)}
                onClear={() => onClear(idx)}
                revealGate={revealGate}
                expectedToken={revealGate ? expectedTokens[idx] : null}
                spacing={codeSlotSpacing(segment, segments[idx + 1] || '')}
                appearance="code"
              />
            ) : null}
          </React.Fragment>
        ))}
        {blankCount > inlineSlotCount &&
          Array.from({ length: blankCount - inlineSlotCount }).map((_, extraIdx) => {
            const idx = inlineSlotCount + extraIdx;
            return (
              <DropSlot
                key={`code-extra-${idx}`}
                slotIndex={idx}
                tokenItem={slots[idx] ?? null}
                disabled={disabled}
                onDropItem={(item) => onDropItem(idx, item)}
                onClear={() => onClear(idx)}
                revealGate={revealGate}
                expectedToken={revealGate ? expectedTokens[idx] : null}
                spacing={{ ml: true, mr: true }}
                appearance="code"
              />
            );
          })}
      </span>
    </pre>
  );
};

export const DragDropPrompt: React.FC<{
  mode: DragDropMode;
  promptLines: string[];
  blankCount: number;
  slots: Array<DragDropSlotValue | null>;
  bankTokens: string[];
  interactionLocked: boolean;
  revealLocked: boolean;
  revealGate: boolean;
  expectedTokens?: Array<string | null>;
  onDropItem: (slotIndex: number, item: DragTokenItem) => void;
  onClear: (slotIndex: number) => void;
  onClickBankToken: (token: string) => void;
}> = ({
  mode,
  promptLines,
  blankCount,
  slots,
  bankTokens,
  interactionLocked,
  revealLocked,
  revealGate,
  expectedTokens = [],
  onDropItem,
  onClear,
  onClickBankToken,
}) => {
  const disabled = interactionLocked || revealLocked;
  const inlineTemplate = useMemo(() => promptLines.join('\n'), [promptLines]);
  const segments = useMemo(() => inlineTemplate.split('____'), [inlineTemplate]);

  return (
    <div className="space-y-3 rounded border border-blue-200 bg-white p-3">
      {mode === 'code_inline' && renderCodeStrip(
        segments,
        blankCount,
        slots,
        disabled,
        revealGate,
        expectedTokens,
        onDropItem,
        onClear,
      )}

      {mode === 'code_order_line' && (
        <>
          {/* In ordering mode, inlineTemplate is treated as an optional prefix (no ____ required). */}
          {renderCodeStrip(
            [inlineTemplate ? `${inlineTemplate} ` : ''],
            blankCount,
            slots,
            disabled,
            revealGate,
            expectedTokens,
            onDropItem,
            onClear,
          )}
          <div className="text-xs text-gray-600">
            Tip: drag tokens into the line (or click tokens to place them). You can drag tokens to reorder.
          </div>
        </>
      )}

      {mode === 'text_inline' && (
        <div className="text-sm text-blue-900">
          {segments.map((segment, idx) => (
            <React.Fragment key={`inline-${idx}`}>
              <span className="whitespace-pre-wrap">{segment}</span>
              {idx < blankCount ? (
                <DropSlot
                  slotIndex={idx}
                  tokenItem={slots[idx] ?? null}
                  disabled={disabled}
                  onDropItem={(item) => onDropItem(idx, item)}
                  onClear={() => onClear(idx)}
                  revealGate={revealGate}
                  expectedToken={revealGate ? expectedTokens[idx] : null}
                />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}

      {mode === 'row_match' && (
        <div className="space-y-2">
          {Array.from({ length: blankCount }).map((_, idx) => (
            <div key={`row-${idx}`} className="flex flex-col gap-1">
              <div className="text-sm text-blue-900 whitespace-pre-wrap">{promptLines[idx] || `Blank ${idx + 1}`}</div>
              <div>
                <DropSlot
                  slotIndex={idx}
                  tokenItem={slots[idx] ?? null}
                  disabled={disabled}
                  onDropItem={(item) => onDropItem(idx, item)}
                  onClear={() => onClear(idx)}
                  revealGate={revealGate}
                  expectedToken={revealGate ? expectedTokens[idx] : null}
                />
              </div>
              {revealGate && expectedTokens[idx] && slots[idx]?.token !== expectedTokens[idx] && (
                <div className="text-xs text-gray-700">Expected: <span className="font-semibold">{expectedTokens[idx]}</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-blue-700">Answer Choices</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {bankTokens.map((token, idx) => (
            <DraggableToken
              key={`${idx}:${token}`}
              token={token}
              disabled={disabled}
              dragItem={{ source: 'bank', token, fromSlotIndex: null }}
              onClick={() => {
                if (revealLocked) return;
                onClickBankToken(token);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
